import { BASE_URL } from "@/constants/endpoints";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import { AxiosInstance } from "axios";

let fertSlotRequestsApiPromise: Promise<AxiosInstance> | null = null;

const getFertSlotRequestsApi = async (): Promise<AxiosInstance | null> => {
  if (fertSlotRequestsApiPromise) return fertSlotRequestsApiPromise;
  fertSlotRequestsApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return fertSlotRequestsApiPromise;
};

// ------------------------------------------------------------
// Endpoints
// ------------------------------------------------------------
// Ajustá este path si tu backend usa otro nombre de controller/route.
const BASE_PATH = "/SolicitudesInsumos";

// ------------------------------------------------------------
// Tipos (mínimos) de Requests/Responses
// ------------------------------------------------------------
export interface PaginationProps {
  "P.Pagina": number;
  "P.Records"?: number;
}

export interface GetFertSlotRequestsProps extends PaginationProps {
  fechaEntrega?: string;
  nroCuenta?: string;
  estados?: number[];
  procedencia?: string;
  kmDistancia?: number;
  cantidad?: number;
  // En backend lo manejan como TipoSolicitud (12 fertilizante, 13 combustible)
  tipoSolicitud: number;
}

export interface GetFertSlotRequestDetailProps {
  id: number;
}

export interface SolicitudProductoDto {
  idProducto: number;
  cantidad: number;
}

export interface CrearPedidoDto {
  lugarDespacho: string;
  URLMaps?: string;
  observaciones: string;
  productos: SolicitudProductoDto[];
}

export interface CrearPedidosSolicitudBody {
  pedidos: CrearPedidoDto[];
}

export interface UpdateSolicitudInsumosBody {
  idEstado: number;
  detalle?: string;
}

export interface UpdatePedidoProductoDto {
  idProducto: number;
  cantidad: number;
}

export interface UpdatePedidoDto {
  idPedido: number;
  lugarDespacho: string;
  URLMaps?: string;
  observaciones: string;
  productos: UpdatePedidoProductoDto[];
}

export interface UpdatePedidosSolicitudBody {
  pedidos: UpdatePedidoDto[];
}

// ------------------------------------------------------------
// API
// ------------------------------------------------------------
export const getFertSlotRequests = async (props: GetFertSlotRequestsProps) => {
  const api = await getFertSlotRequestsApi();
  if (!api) return;

  const response = await api.get(`${BASE_PATH}?${objectToQueryStringWithArray(props)}`);
  return response.data;
};

export const getFertSlotRequestDetail = async (props: GetFertSlotRequestDetailProps) => {
  const api = await getFertSlotRequestsApi();
  if (!api) return;

  const response = await api.get(`${BASE_PATH}/${props.id}`);
  return response.data;
};

// GET /api/SolicitudesInsumos/{id}/pedidos
export const getFertSlotRequestOrdersDetail = async (idSolicitud: number) => {
  const api = await getFertSlotRequestsApi();
  if (!api) return;

  const response = await api.get(`${BASE_PATH}/${idSolicitud}/pedidos`);
  return response.data;
};

// Crear pedidos para una solicitud: POST /{id}/pedidos
export const createFertSlotRequestOrders = async (idSolicitud: number, body: CrearPedidosSolicitudBody) => {
  const api = await getFertSlotRequestsApi();
  if (!api) return;

  const response = await api.post(`${BASE_PATH}/${idSolicitud}/pedidos`, body);
  return response.data;
};

// Editar pedidos para una solicitud: PUT /{id}/pedidos
export const updateFertSlotRequestOrders = async (idSolicitud: number, body: UpdatePedidosSolicitudBody) => {
  const api = await getFertSlotRequestsApi();
  if (!api) return;

  const response = await api.put(`${BASE_PATH}/${idSolicitud}/pedidos`, body);
  return response.data;
};

// Actualizar solicitud (estado y/o mensaje): PUT /{id}
export const updateFertSlotRequest = async (idSolicitud: number, body: UpdateSolicitudInsumosBody) => {
  const api = await getFertSlotRequestsApi();
  if (!api) return;

  const response = await api.put(`${BASE_PATH}/${idSolicitud}`, body);
  return response.data;
};

// Asignar pedido: PUT /api/SolicitudesInsumos/{idPedido}/asignar
export interface ConfirmFertSlotOrderBody {
  fechaEntrega: string;
  idChofer: number;
  idEquipo: number;
}

export const confirmFertSlotOrder = async (
  idPedido: number,
  body: ConfirmFertSlotOrderBody,
) => {
  const api = await getFertSlotRequestsApi();
  if (!api) return;

  const response = await api.put(`${BASE_PATH}/${idPedido}/asignar`, body);
  return response.data;
};

export const reassignFertSlotOrder = async (
  idPedido: number,
  body: ConfirmFertSlotOrderBody,
) => {
  const api = await getFertSlotRequestsApi();
  if (!api) return;

  const response = await api.put(`${BASE_PATH}/${idPedido}/reasignar`, body);
  return response.data;
};
